import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import axios from 'axios'
import { useAuthStore } from '@/store/authStore'
import { useThemeSync } from '@/hooks/useTheme'
import { FullPageSpinner } from '@/components/ui/Spinner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { apiUrl } from '@/api/config'
import type { UserRole } from '@/types'

// ─────────────────────────────────────────────
// Lazy-loaded pages
// ─────────────────────────────────────────────

// Auth
const Login          = lazy(() => import('@/pages/auth/Login'))
const Register       = lazy(() => import('@/pages/auth/Register'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ResetPassword  = lazy(() => import('@/pages/auth/ResetPassword'))

// Practitioner
const PractitionerLayout    = lazy(() => import('@/components/layout/PractitionerLayout'))
const PractitionerDashboard = lazy(() => import('@/pages/practitioner/Dashboard'))
const Clients               = lazy(() => import('@/pages/practitioner/Clients'))
const Settings              = lazy(() => import('@/pages/practitioner/Settings'))
const Schedule              = lazy(() => import('@/pages/practitioner/Schedule'))
const Notes                 = lazy(() => import('@/pages/practitioner/Notes'))
const PractitionerMessages  = lazy(() => import('@/pages/practitioner/Messages'))
const Forms                 = lazy(() => import('@/pages/practitioner/Forms'))
const PractitionerInvoices  = lazy(() => import('@/pages/practitioner/Invoices'))
const ComingSoon            = lazy(() => import('@/pages/practitioner/ComingSoon'))
const Protocols             = lazy(() => import('@/pages/practitioner/Protocols'))
const ProtocolDetail        = lazy(() => import('@/pages/practitioner/ProtocolDetail'))
const JournalViewer         = lazy(() => import('@/pages/practitioner/JournalViewer'))

// Admin
const AdminLayout           = lazy(() => import('@/components/layout/AdminLayout'))
const AdminDashboard        = lazy(() => import('@/pages/admin/AdminDashboard'))
const AdminPractitioners    = lazy(() => import('@/pages/admin/AdminPractitioners'))
const AdminReports          = lazy(() => import('@/pages/admin/AdminReports'))
const AdminSecurity         = lazy(() => import('@/pages/admin/AdminSecurity'))
const AdminAiProviders      = lazy(() => import('@/pages/admin/AdminAiProviders'))

// Client
const ClientLayout          = lazy(() => import('@/components/layout/ClientLayout'))
const ClientPortal          = lazy(() => import('@/pages/client/Portal'))
const ClientAppointments    = lazy(() => import('@/pages/client/ClientAppointments'))
const ClientMessages        = lazy(() => import('@/pages/client/ClientMessages'))
const ClientForms           = lazy(() => import('@/pages/client/ClientForms'))
const ClientInvoices        = lazy(() => import('@/pages/client/ClientInvoices'))
const ClientProfile         = lazy(() => import('@/pages/client/ClientProfile'))
const MyProtocol            = lazy(() => import('@/pages/client/MyProtocol'))
const ClientJournal         = lazy(() => import('@/pages/client/Journal'))
const ClientNotes           = lazy(() => import('@/pages/client/ClientNotes'))

// Public
const LandingPage = lazy(() => import('@/pages/public/LandingPage'))
const BookingPage = lazy(() => import('@/pages/public/BookingPage'))
const Terms       = lazy(() => import('@/pages/public/Terms'))
const Privacy     = lazy(() => import('@/pages/public/Privacy'))

// Global
const NotFound = lazy(() => import('@/pages/NotFound'))

// ─────────────────────────────────────────────
// Session restore on page reload
// ─────────────────────────────────────────────
function useSessionRestore() {
  const { user, accessToken, setAccessToken, logout } = useAuthStore()
  useEffect(() => {
    if (user && !accessToken) {
      axios
        .post<{ data: { accessToken: string } }>(apiUrl('/auth/refresh'), {}, { withCredentials: true })
        .then(({ data }) => setAccessToken(data.data.accessToken))
        .catch(() => logout())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

// ─────────────────────────────────────────────
// Route guards
// ─────────────────────────────────────────────
function ProtectedRoute({ role, children }: { role?: UserRole; children: JSX.Element }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (role && user.role !== role) {
    if (user.role === 'ADMIN') return <Navigate to="/admin" replace />
    return <Navigate to={user.role === 'PRACTITIONER' ? '/dashboard' : '/portal'} replace />
  }
  return children
}

function GuestRoute({ children }: { children: JSX.Element }) {
  const { user } = useAuthStore()
  if (user) {
    if (user.role === 'ADMIN') return <Navigate to="/admin" replace />
    return <Navigate to={user.role === 'PRACTITIONER' ? '/dashboard' : '/portal'} replace />
  }
  return children
}

function RootRedirect() {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (user.role === 'ADMIN') return <Navigate to="/admin" replace />
  return <Navigate to={user.role === 'PRACTITIONER' ? '/dashboard' : '/portal'} replace />
}

// ─────────────────────────────────────────────
// App routes
// ─────────────────────────────────────────────
function AppRoutes() {
  useSessionRestore()
  useThemeSync()

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* ── Auth ── */}
        <Route path="/login"           element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/register"        element={<GuestRoute><Register /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password"  element={<ResetPassword />} />

        {/* ── Admin ── */}
        <Route
          path="/admin"
          element={<ProtectedRoute role="ADMIN"><AdminLayout /></ProtectedRoute>}
        >
          <Route index element={<AdminDashboard />} />
          <Route path="practitioners"  element={<AdminPractitioners />} />
          <Route path="reports"        element={<AdminReports />} />
          <Route path="security"       element={<AdminSecurity />} />
          <Route path="ai-providers"   element={<AdminAiProviders />} />
        </Route>

        {/* ── Practitioner ── */}
        <Route
          path="/dashboard"
          element={<ProtectedRoute role="PRACTITIONER"><PractitionerLayout /></ProtectedRoute>}
        >
          <Route index    element={<PractitionerDashboard />} />
          <Route path="clients"          element={<Clients />} />
          <Route path="settings"         element={<Settings />} />
          <Route path="schedule"         element={<Schedule />} />
          <Route path="notes"            element={<Notes />} />
          <Route path="messages"         element={<PractitionerMessages />} />
          <Route path="forms"            element={<Forms />} />
          <Route path="invoices"         element={<PractitionerInvoices />} />
          <Route path="protocols"        element={<Protocols />} />
          <Route path="protocols/:id"    element={<ProtocolDetail />} />
          <Route path="journal"          element={<JournalViewer />} />
        </Route>

        {/* ── Client portal ── */}
        <Route
          path="/portal"
          element={<ProtectedRoute role="CLIENT"><ClientLayout /></ProtectedRoute>}
        >
          <Route index                element={<ClientPortal />} />
          <Route path="appointments"  element={<ClientAppointments />} />
          <Route path="messages"      element={<ClientMessages />} />
          <Route path="forms"         element={<ClientForms />} />
          <Route path="invoices"      element={<ClientInvoices />} />
          <Route path="profile"       element={<ClientProfile />} />
          <Route path="protocol"      element={<MyProtocol />} />
          <Route path="journal"       element={<ClientJournal />} />
          <Route path="notes"         element={<ClientNotes />} />
        </Route>

        {/* ── Public booking ── */}
        <Route path="/book/:slug" element={<BookingPage />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Toaster
        position="top-center"
        gutter={8}
        containerStyle={{ top: 56 }}
        toastOptions={{
          duration: 4000,
          style: {
            background: 'white',
            color: '#1E293B',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '380px',
          },
          success: { iconTheme: { primary: '#2DBE6C', secondary: 'white' } },
          error:   { iconTheme: { primary: '#EF4444', secondary: 'white' } },
        }}
      />
      <AppRoutes />
    </BrowserRouter>
    </ErrorBoundary>
  )
}
